"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DrawManager = void 0;
const rxjs_1 = require("rxjs");
const config_json_1 = __importDefault(require("../../../shared/config.json"));
const Global_1 = require("../Global");
const lodash_1 = __importDefault(require("lodash"));
class DrawManager {
    constructor(draw, gameManager) {
        this.draw = draw;
        this.gameManager = gameManager;
        this.updated$ = new rxjs_1.Subject();
        this.erased$ = new rxjs_1.Subject();
        this.subs = new rxjs_1.Subscription();
        this.canvas = this.makeCanvas();
        this.ctx = this.canvas.getContext('2d', {
            alpha: false,
            desynchronized: true,
            willReadFrequently: true
        });
        this.template = lodash_1.default.times(this.canvas.height, () => lodash_1.default.times(this.canvas.width, () => 0.000));
        this.ctx.imageSmoothingEnabled = true;
        this.ctx.imageSmoothingQuality = 'high';
        this.ctx.lineCap = this.ctx.lineJoin = 'round';
        this.ctx.lineWidth = 1.5;
        this.ctx.strokeStyle = `rgb(255, 255, 255)`;
        this.ctx.fillStyle = `rgb(0, 0, 0)`;
        this.subs.add(this.draw.strokes$.subscribe({
            next: (strokes) => {
                this.clear();
                let percent = null;
                if (strokes.length) {
                    // Just in case, we should be playing now, triggers for Poki
                    if (!this.gameManager.isPlaying) {
                        this.gameManager.startPlay();
                    }
                    this.drawStrokes(strokes);
                    const size = this.draw.size;
                    const laststroke = lodash_1.default.last(strokes);
                    const lastpt = lodash_1.default.last(laststroke);
                    percent = [
                        lastpt[0] / (0, Global_1.px)(size.width),
                        lastpt[1] / (0, Global_1.px)(size.height)
                    ];
                }
                else {
                    this.erased$.next();
                }
                const encoded = this.encode(percent, Math.max(strokes.length - 1, 0));
                this.updated$.next(encoded);
            }
        }));
    }
    makeCanvas() {
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = config_json_1.default.canvassize;
        return canvas;
    }
    clear() {
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        // this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
    drawStrokes(strokes, padding = 2) {
        const tw = this.canvas.width - padding * 2;
        const th = this.canvas.height - padding * 2;
        const box = this.draw.bounding$.value;
        const boxw = (box.right - box.left) || 1;
        const boxh = (box.bottom - box.top) || 1;
        const aspectw = boxh > boxw ? boxw / boxh : 1;
        const aspecth = boxw > boxh ? boxh / boxw : 1;
        const offsetX = ((tw - tw * aspectw) / 2) + padding;
        const offsetY = ((th - th * aspecth) / 2) + padding;
        const scaledxy = (x, y) => {
            const tx = ((x - box.left) / boxw) * aspectw;
            const ty = ((y - box.top) / boxh) * aspecth;
            const translatedX = tx * tw + offsetX;
            const translatedY = ty * th + offsetY;
            return [translatedX, translatedY];
        };
        for (let stroke of strokes) {
            if (stroke.length > 1) {
                this.ctx.beginPath();
                const pt = scaledxy(stroke[0][0], stroke[0][1]);
                this.ctx.moveTo(pt[0], pt[1]);
                for (let i = 1; i < stroke.length; i++) {
                    const pt = scaledxy(stroke[i][0], stroke[i][1]);
                    this.ctx.lineTo(pt[0], pt[1]);
                }
                this.ctx.stroke();
            }
        }
    }
    encode(percent = null, strokeid = 0) {
        const pixelsChanged = this.encodedChanges;
        const encodedPixels = pixelsChanged.join('');
        const encodedPoint = percent ? strokeid.toString().padStart(config_json_1.default.pointdigits, '0')
            + ((percent[0].toFixed(config_json_1.default.pointdigits))
                + ',' + (percent[1].toFixed(config_json_1.default.pointdigits))) : 'D';
        return (encodedPoint + '|' + encodedPixels);
    }
    rgbToa(r, g, b) {
        return ((0.21 * r) + (0.72 * g) + (0.07 * b)) / 255;
    }
    get encodedChanges() {
        const imageData = this.imageData;
        const data = imageData.data;
        const changed = [];
        let j = 0;
        let i = 0;
        for (let d = 0; d < data.length; d += 4, j++) {
            const r = data[d];
            const g = data[d + 1];
            const b = data[d + 2];
            // const a = data[d+3];
            if (j >= imageData.width) {
                j = 0;
                i++;
            }
            const element = this.rgbToa(r, g, b);
            if (element !== this.template[i][j]) {
                // This pixel has changed
                const encodedNumber = (element.toFixed(config_json_1.default.pointdigits));
                const encoded = i.toString().padStart(DrawManager.Digits, '0')
                    + j.toString().padStart(DrawManager.Digits, '0') + encodedNumber;
                changed.push(encoded);
                this.template[i][j] = element;
            }
        }
        return changed;
    }
    get imageData() {
        return this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
    }
    get scaled() {
        return this.canvas;
    }
    destroy() {
        var _a;
        (_a = this.canvas) === null || _a === void 0 ? void 0 : _a.remove();
        this.subs.unsubscribe();
    }
}
exports.DrawManager = DrawManager;
DrawManager.Digits = config_json_1.default.canvassize.toString().length;
